/**
 * Document block — PDFs, plain text, CSV etc.
 *
 * Renders as a single inline line ``Attached document.pdf · 2.3 MB``
 * in the chat column, matching the inline-prose model the tool rows
 * use. Clicking opens the underlying URL / data URL in a new tab;
 * base64 sources become Blob URLs on demand so they download
 * correctly. No card chrome at rest — the filename + tail metadata
 * is the whole row, and a faint underline on hover reveals it as a
 * link.
 */

import { FileText } from 'lucide-react';
import * as React from 'react';
import type { DocumentBlock, MediaSource } from '@/lib/agent/types';
import { browserMediaUrl } from '@/lib/media-utils/browserMediaUrl';
import { cn } from '@/lib/utils/cn';

export interface DocumentBlockViewProps {
  block: DocumentBlock;
  className?: string;
}

export function DocumentBlockView({ block, className }: DocumentBlockViewProps) {
  const href = useHref(block.source);
  const mediaType = 'media_type' in block.source ? block.source.media_type : undefined;
  const displayName = block.title ?? fallbackName(mediaType);
  const shortType = mediaTypeShort(mediaType) ?? extensionOf(displayName) ?? 'file';
  const sizeLabel = block.size != null ? formatBytes(block.size) : null;
  const tail = `${shortType}${sizeLabel ? ` · ${sizeLabel}` : ''}`;

  // All inline text collapses into a SINGLE flex-item span so the
  // row copies as one line ("Attached document.pdf · 2.3 MB")
  // instead of one word per line — flex items get blockified for
  // selection. The ``FileText`` icon stays an outboard fixture.
  const body = (
    <>
      <FileText className="size-3 shrink-0 self-center text-gray-400 dark:text-gray-500" aria-hidden />
      <span className="min-w-0 truncate">
        <span className="font-medium text-gray-700 dark:text-gray-200">Attached</span>{' '}
        <span className="font-normal">{displayName}</span>{' '}
        <span className="text-gray-400 dark:text-gray-500">· {tail}</span>
      </span>
    </>
  );

  const common = cn(
    '-mx-1 inline-flex max-w-full select-text items-baseline gap-1 rounded px-1 py-px text-[14px] text-gray-600 dark:text-gray-300',
    className
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(common, 'cursor-pointer hover:bg-gray-100/70 dark:hover:bg-white/[0.04]')}
      >
        {body}
      </a>
    );
  }
  return <div className={common}>{body}</div>;
}

function useHref(source: MediaSource): string | null {
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (source.type !== 'base64' || !source.data) {
      setBlobUrl(null);
      return;
    }
    try {
      const bytes = Uint8Array.from(atob(source.data), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: safeBlobMediaType(source.media_type) });
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
      return () => URL.revokeObjectURL(url);
    } catch {
      setBlobUrl(null);
    }
  }, [source]);

  if (source.type === 'url') return browserMediaUrl(source.url);
  if (source.type === 'base64') return blobUrl;
  return null;
}

function safeBlobMediaType(mediaType: string | undefined): string {
  if (!mediaType) return 'application/octet-stream';
  if (mediaType === 'text/html' || mediaType === 'image/svg+xml') return 'application/octet-stream';
  return mediaType;
}

function fallbackName(mediaType?: string): string {
  if (!mediaType) return 'document';
  if (mediaType === 'application/pdf') return 'document.pdf';
  if (mediaType.startsWith('text/')) return `document.${mediaType.split('/')[1]}`;
  return 'document';
}

function mediaTypeShort(mediaType?: string): string | null {
  if (!mediaType) return null;
  const sub = mediaType.split('/')[1] ?? mediaType;
  return sub.length > 8 ? sub.slice(0, 8) : sub;
}

function extensionOf(filename: string): string | null {
  const dot = filename.lastIndexOf('.');
  if (dot < 0 || dot === filename.length - 1) return null;
  const ext = filename.slice(dot + 1);
  return ext.length > 8 ? null : ext;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
